import { useState } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/common";
import { LoadBadge } from "@/components/StatusPill";
import { MapPin, Plus, History } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BLANK = { name: "", external_code: "", load_class: "medel", repetitivity: "medel",
  min_staffing: 1, desired_staffing: 2, rotation_limit_pct: 40, max_continuous_minutes: 240, min_rest_minutes: 30 };

export default function Stations() {
  const { effectiveRole } = useAuth();
  const canEdit = ["owner", "hr_admin"].includes(effectiveRole);
  const { data: stations, loading, reload } = useApi("/stations");
  const { data: comps } = useApi("/competences");
  const { data: rules, reload: reloadRules } = useApi("/rotation-rules");
  const [form, setForm] = useState(BLANK);
  const [reqComp, setReqComp] = useState([]);
  const [open, setOpen] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      await api.post("/stations", { ...form,
        min_staffing: Number(form.min_staffing), desired_staffing: Number(form.desired_staffing),
        rotation_limit_pct: Number(form.rotation_limit_pct),
        required_competences: reqComp.map((id) => ({ competence_id: id, min_level: 1 })) });
      setForm(BLANK); setReqComp([]); setOpen(false); reload(); toast.success("Station skapad");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const rule = rules?.[0];
  const updateRule = async (k, v) => {
    try { await api.patch(`/rotation-rules/${rule.id}`, { [k]: Number(v) }); reloadRules(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader title="Stationer & belastningsklassificering" icon={MapPin}
        subtitle="Versionshanterad klassificering. Historiska beräkningar använder den version som gällde vid arbetstillfället.">
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-station-btn"><Plus className="mr-1 h-4 w-4" />Ny station</Button></DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Ny station / arbetsområde</DialogTitle></DialogHeader>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2"><Label>Namn</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="station-name" /></div>
                <div><Label>Extern systemkod</Label><Input value={form.external_code} onChange={(e) => set("external_code", e.target.value)} data-testid="station-code" /></div>
                <div><Label>Belastningsklass</Label>
                  <Select value={form.load_class} onValueChange={(v) => set("load_class", v)}>
                    <SelectTrigger data-testid="station-load"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="latt">Lätt</SelectItem><SelectItem value="medel">Medel</SelectItem><SelectItem value="tung">Tung</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Repetitivitet</Label>
                  <Select value={form.repetitivity} onValueChange={(v) => set("repetitivity", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="låg">Låg</SelectItem><SelectItem value="medel">Medel</SelectItem><SelectItem value="hög">Hög</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Rotationsgräns (% tung)</Label><Input type="number" value={form.rotation_limit_pct} onChange={(e) => set("rotation_limit_pct", e.target.value)} data-testid="station-limit" /></div>
                <div><Label>Minsta bemanning</Label><Input type="number" value={form.min_staffing} onChange={(e) => set("min_staffing", e.target.value)} /></div>
                <div><Label>Önskad bemanning</Label><Input type="number" value={form.desired_staffing} onChange={(e) => set("desired_staffing", e.target.value)} /></div>
                <div className="sm:col-span-2">
                  <Label>Obligatoriska kompetenser</Label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {(comps || []).map((c) => (
                      <button key={c.id} type="button" onClick={() => setReqComp((r) => r.includes(c.id) ? r.filter((x) => x !== c.id) : [...r, c.id])}
                        className={`rounded-full border px-3 py-1 text-xs ${reqComp.includes(c.id) ? "border-accent bg-accent/10 text-accent" : "text-muted-foreground"}`}
                        data-testid={`req-comp-${c.id}`}>{c.name}</button>
                    ))}
                    {(comps || []).length === 0 && <span className="text-xs text-muted-foreground">Lägg till kompetenser först.</span>}
                  </div>
                </div>
              </div>
              <DialogFooter><Button onClick={save} data-testid="save-station">Spara station</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      {rule && canEdit && (
        <Card className="mb-4 p-4">
          <div className="mb-3 text-sm font-semibold">Rotationsregel (organisation)</div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div><Label className="text-xs">Tung-gräns %</Label><Input type="number" defaultValue={rule.heavy_limit_pct} onBlur={(e) => updateRule("heavy_limit_pct", e.target.value)} data-testid="rule-heavy-limit" /></div>
            <div><Label className="text-xs">Gul-marginal %</Label><Input type="number" defaultValue={rule.near_margin_pct} onBlur={(e) => updateRule("near_margin_pct", e.target.value)} /></div>
            <div><Label className="text-xs">Period (dagar)</Label><Input type="number" defaultValue={rule.period_days} onBlur={(e) => updateRule("period_days", e.target.value)} /></div>
            <div><Label className="text-xs">Max sammanh. min</Label><Input type="number" defaultValue={rule.max_continuous_minutes} onBlur={(e) => updateRule("max_continuous_minutes", e.target.value)} /></div>
          </div>
        </Card>
      )}

      {loading ? <p className="text-muted-foreground">Laddar…</p> : (stations || []).length === 0 ? (
        <EmptyState icon={MapPin} title="Inga stationer ännu" text="Skapa arbetsområden och klassificera belastning." />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {stations.map((s) => (
            <Card key={s.id} className="p-4" data-testid={`station-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-head font-semibold">{s.name}</div>
                  {s.external_code && <div className="font-mono text-xs text-muted-foreground">{s.external_code}</div>}
                </div>
                <LoadBadge cls={s.load_class} />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <div>Rotationsgräns: <span className="font-mono font-semibold text-foreground">{s.rotation_limit_pct}%</span></div>
                <div>Bemanning: <span className="font-mono font-semibold text-foreground">{s.min_staffing}–{s.desired_staffing}</span></div>
                <div>Repetitivitet: <span className="text-foreground">{s.repetitivity}</span></div>
                <div className="flex items-center gap-1"><History className="h-3 w-3" />v{s.version}</div>
              </div>
              {(s.required_competences || []).length > 0 && (
                <div className="mt-2 text-xs text-muted-foreground">Krav: {(s.required_competences || []).length} kompetens(er)</div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
